/**
 * Orphaned-session sign-out bridge.
 *
 * When the Aelo user row is deleted server-side while a Clerk session is still
 * live in some tab, every API call 401s with `code: 'user_not_found'` — forever
 * (background polls like the notifications count turn that into an error storm).
 * The fetch layer can't call Clerk hooks, so it notifies through this module and
 * the Clerk app root registers the actual `signOut` handler
 * (see features/auth/OrphanedSessionSignOut). Embedded (Bitrix24) mode never
 * registers a handler, so notifications are a no-op there.
 */

let handler: (() => void) | null = null;
let fired = false;

/** Register the sign-out handler. Returns an unregister function. */
export function registerOrphanedSessionHandler(next: () => void): () => void {
  handler = next;
  fired = false;
  return () => {
    if (handler === next) handler = null;
  };
}

/**
 * One-shot: concurrent polls all hit 401 together — only the first may trigger
 * sign-out, the rest must not queue duplicate redirects.
 */
export function notifyOrphanedSession(): void {
  if (fired || !handler) return;
  fired = true;
  handler();
}

/** True when a 401 body marks the session as pointing at a deleted user. */
export function isOrphanedSessionBody(errorData: unknown): boolean {
  return (
    typeof errorData === 'object' &&
    errorData !== null &&
    (errorData as { code?: unknown }).code === 'user_not_found'
  );
}
